import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges, OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from "@angular/core";
import {
	CellClickEvent,
	GridComponent,
	GridDataResult,
	PageChangeEvent,
	SortSettings
} from "@progress/kendo-angular-grid";
import {IMultiselectGridColumn} from "../interfaces/imultiselect.grid.column";
import {MarkOperator} from "../../../services/mark.service";
import {IMultiselectGridLoadParameters} from "../interfaces/imultiselect.grid.load.parameters";
import {SortDescriptor, State} from "@progress/kendo-data-query";
import { SearchBoxComponent } from "../../search.box/search.box.component";
import { SystemConfigurationValues } from "common/configuration/services/system.configuration.utils";
import {SortOrder} from "../../../../../app.services/lookups/interfaces/Isort.lookup";
import {distinctUntilChanged, switchMap, throttleTime} from "rxjs/operators";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {asyncScheduler, Subject} from "rxjs";


@UntilDestroy()
@Component({
	selector: 'cp-multiselect-grid',
	styleUrls: ['./multiselect.grid.style.scss'],
	templateUrl: './multiselect.grid.template.html',
})
export class MultiSelectGridComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

	@Input() dialogTitle: string;
	@Input() unselectedDataItems: any[] = null;
	@Input() selectedDataItems: any[]= [];
	@Input() totalUnselectedItemsCount: number;
	@Input() gridColumns: IMultiselectGridColumn[];
	@Input() availableItemsTranslationKey: string;
	@Input() selectedItemsTranslationKey: string;
	@Input() serverSideSearchEnabled: boolean = false;
	@Input() searchEnabled: boolean = true;
	@Input() headerMessageTranslationKey: string;

	@Input() availableItemsGridSortSettings: SortSettings;
	@Input() primaryKeyColumn: string;
	@Input() maxSelectAllItems: number = null;
	@Input() dialogHeight: string = '760px';
	@Input() dialogWidth: string = '1423px';
	@Input() pageSize: number;

	@Output() onCancel: EventEmitter<void> = new EventEmitter<void>();
	@Output() onSubmit: EventEmitter<any[]> = new EventEmitter<any[]>();
	@Output() onLoadParametersChange: EventEmitter<IMultiselectGridLoadParameters> = new EventEmitter<IMultiselectGridLoadParameters>();

	@ViewChild("searchBox",{static:false}) searchBox: SearchBoxComponent;
	@ViewChild("availableItemsGrid", {static:false}) gridComponent: GridComponent;

	public filteredUnselectedDataItems: GridDataResult;
	public searchTerm: string;
	public markOperator = MarkOperator;
	public isDataLoading: boolean = true;
	public addAllItemsEnabled: boolean = false;
	public removeAllItemsEnabled: boolean = false;
	public readonly gridHeight: number = 495;
	public readonly gridRowHeight: number = 40;

	public availableItemsGridState: State;
	public availableItemsSkip: number = 0;

	private loadParametersChangedSubject: Subject<any>;
	private addAllItemsAfterLoading: boolean = false;

	constructor(private cd: ChangeDetectorRef,
		private systemCfg: SystemConfigurationValues) {
	}

	ngOnInit(): void {
		this.searchTerm = '';

		if (!this.pageSize) {
			this.pageSize = Math.max(this.maxSelectAllItems,  Math.ceil(this.gridHeight / this.gridRowHeight) * 2);
		}
		this.availableItemsGridState = {skip:0,
			take: this.pageSize,
			sort: this.getColumnsSort()};

		if (this.unselectedDataItems && this.unselectedDataItems.length) {
			this.isDataLoading = false;
		}
		if (this.maxSelectAllItems === null) {
			this.maxSelectAllItems = this.systemCfg.getNumber('MaxSelectAllMultiselectItems', 100);
		}

		this.subscribeToLoadParametersSubject();
		this.updateAllItemsButtonsEnabled();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["unselectedDataItems"]) {
			this.updatedItemsLists();

			if (!changes["unselectedDataItems"].firstChange) { //We don't want to disable the loading spinner for the initial value
				this.isDataLoading = false;
			}

			if (this.addAllItemsAfterLoading) {
				this.addAllItemsAfterLoading = false;
				this.addAllItems();
			}
		}

		if (changes["selectedDataItems"] && !changes["selectedDataItems"].firstChange) {
			this.updateAllItemsButtonsEnabled();
		}
	}

	ngAfterViewInit() {
		this.gridComponent.pageChange.pipe(
			distinctUntilChanged())
			.pipe(untilDestroyed(this))
			.subscribe((e) => this.onAvailableItemsPageChanged(e));

	}

	ngOnDestroy() {

	}

	onSearchChanged($event: string) {
		if ($event === this.searchTerm) {
			return;
		}

		this.searchTerm = $event;
		if (this.serverSideSearchEnabled) {
			this.availableItemsSkip = 0;
			this.emitLoadParametersChanged();
		} else { //Perform client side filtering
			this.updateFilteredNonSelectedItems();
			this.cd.markForCheck();
		}
	}

	public isValidDataForSubmit(): boolean {
		return true;
		//return this.selectedDataItems.length > 0;
	}

	public onEditorDone() {
		this.onSubmit.emit(this.selectedDataItems);
	}

	onAvailableItemsCellClick($event: CellClickEvent) {

		//We don't want to enable selection when loading is in progress
		if (this.isDataLoading) return;

		let selectedDataItem = $event.dataItem;
		let selectedItemIndex = this.unselectedDataItems.indexOf(selectedDataItem);
		this.unselectedDataItems.splice(selectedItemIndex, 1);

		this.selectedDataItems.push(selectedDataItem);
		this.totalUnselectedItemsCount--;
		this.updatedItemsLists();

		if (this.serverSideSearchEnabled) {
			this.loadParametersChangedSubject.next();
		}
	}

	onSelectedItemsCellClick($event: CellClickEvent) {
		let selectedDataItem = $event.dataItem;
		let selectedItemIndex = this.selectedDataItems.indexOf(selectedDataItem);
		this.selectedDataItems.splice(selectedItemIndex, 1);
		this.totalUnselectedItemsCount++;

		if (!this.serverSideSearchEnabled) {
			this.unselectedDataItems.push(selectedDataItem);
		}

		this.updatedItemsLists();

		if (this.serverSideSearchEnabled) {
			this.isDataLoading = true;
			this.loadParametersChangedSubject.next();
		}

	}

	onAvailableItemsPageChanged($event: PageChangeEvent) {
		this.updatePageSkip($event.skip);
	}

	onAvailableItemsSortChanged($event: Array<SortDescriptor>) {
		this.availableItemsGridState = {...this.availableItemsGridState};
		this.availableItemsGridState.sort = $event.filter((s) => s.dir);
		setTimeout(() => {
			this.emitLoadParametersChanged();
		});

	}

	addAllItems() {

		if (this.filteredUnselectedDataItems.data.length === this.filteredUnselectedDataItems.total) {
			//We have all items in the client side
			this.unselectedDataItems.forEach(item => {
				this.selectedDataItems.push(item);
			});

			this.unselectedDataItems = [];

			if (this.searchTerm) {
				this.searchBox.clearSearch();
			} else {
				this.updatedItemsLists();
			}
			this.emitLoadParametersChanged();

		} else { //We need to fetch all items
			this.addAllItemsAfterLoading = true;
			this.emitLoadAllDataParametersChanged();
		}
	}

	removeAllItems() {
		this.selectedDataItems = [];

		if (this.searchTerm) {
			this.searchBox.clearSearch();
		} else {
			this.updatedItemsLists();
		}
		this.emitLoadParametersChanged();
	}

	private updatePageSkip(skip: number) {
		this.availableItemsSkip = skip;
		this.emitLoadParametersChanged();
	}

	private updateFilteredNonSelectedItems() {
		if (this.searchTerm) {
			this.filteredUnselectedDataItems.data = this.unselectedDataItems.filter(item => {
					for (let prop of Object.getOwnPropertyNames(item)) {
						if (item[prop] == null) {
							continue;
						}
						if (item[prop].toString().toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1) {
							return true;
						}
					}
					return false;
				}
			);
		} else {
			this.filteredUnselectedDataItems.data = this.unselectedDataItems
		}

	}

	private GetLoadParameters() {
		let startIndex = this.availableItemsSkip;
		let	endIndex = this.availableItemsSkip + this.pageSize;

		const loadParameters: IMultiselectGridLoadParameters = {
			pageStartIndex: startIndex,
			pageEndIndex: endIndex,
			searchTerm: this.searchTerm,
			sort: this.availableItemsGridState.sort,
			selectedKeys: this.getSelectedItemKeys()

		};

		return loadParameters;
	}

	private updateAllItemsButtonsEnabled() {
		if (this.maxSelectAllItems) {
			this.addAllItemsEnabled =  this.filteredUnselectedDataItems && (this.filteredUnselectedDataItems.total > 0)
			&& this.maxSelectAllItems >=  this.filteredUnselectedDataItems.total;
		} else {
			this.addAllItemsEnabled = this.filteredUnselectedDataItems &&  (this.filteredUnselectedDataItems.total > 0);
		}

		this.removeAllItemsEnabled = this.selectedDataItems && (this.selectedDataItems.length > 0);

	}

	private updatedItemsLists() {
		this.filteredUnselectedDataItems = {
			total: this.totalUnselectedItemsCount,
			data:  this.unselectedDataItems
		};
		this.updateAllItemsButtonsEnabled();
	}

	private getSelectedItemKeys(): string[] {
		return this.selectedDataItems.map(item => item[this.primaryKeyColumn].toString());
	}


	private getColumnsSort(): Array<SortDescriptor> {
		let sortResult = [];
		this.gridColumns.forEach(column => {

			if (column.columnSort != null) {
				sortResult.push({
					field: column.columnName,
					dir:  column.columnSort === SortOrder.asc ? 'asc' : 'desc'
				})
			}
		});
		return sortResult;
	}


	private emitLoadAllDataParametersChanged() {
		this.isDataLoading = true;
		let loadParameters: IMultiselectGridLoadParameters =  {
			pageStartIndex : 0,
			pageEndIndex: this.totalUnselectedItemsCount,
			searchTerm: this.searchTerm,
			sort: this.availableItemsGridState.sort,
			selectedKeys: this.getSelectedItemKeys()
		};
		this.onLoadParametersChange.emit(loadParameters);
	}

	private emitLoadParametersChanged() {
		this.isDataLoading = true;
		let loadParameters: IMultiselectGridLoadParameters = this.GetLoadParameters();
		this.onLoadParametersChange.emit(loadParameters);
	}

	private subscribeToLoadParametersSubject() {
		this.loadParametersChangedSubject = new Subject();

		this.loadParametersChangedSubject.asObservable().pipe(
			throttleTime(3000, asyncScheduler,  { leading: false, trailing: true }))
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.emitLoadParametersChanged();
			});
	}
}

